import {
  ClusterOutlined,
  IdcardOutlined,
  MedicineBoxOutlined,
} from '@ant-design/icons';
import { Descriptions, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  formatDisplayValue,
  severityTagColor,
} from '~/app/components/errorManagement/ErrorManagementPage/utils';
import type { VendorErrorCodeCatalogFields } from '~/app/components/errorManagement/VendorErrorCodeCatalogSections/types';
import {
  ViewDrawerSectionHeader,
  type ViewDrawerSectionTone,
} from '~/app/components/errorManagement/ViewDrawerSectionHeader/ViewDrawerSectionHeader';

const SECTION_TONES = {
  identity: {
    accent: 'var(--evimo-blue)',
    bg: 'color-mix(in srgb, var(--evimo-blue) 8%, transparent)',
    icon: <IdcardOutlined />,
  },
  diagnostics: {
    accent: 'var(--evimo-success)',
    bg: 'color-mix(in srgb, var(--evimo-success) 8%, transparent)',
    icon: <MedicineBoxOutlined />,
  },
  classification: {
    accent: 'var(--evimo-warning)',
    bg: 'color-mix(in srgb, var(--evimo-warning) 8%, transparent)',
    icon: <ClusterOutlined />,
  },
} as const satisfies Record<string, ViewDrawerSectionTone>;

export interface VendorErrorCodeCatalogSectionsProps {
  record: VendorErrorCodeCatalogFields;
  /** When false, classification Descriptions omit bottom margin (last section). */
  classificationMarginBottom?: boolean;
  /** Unresolved queue only — catalog rows do not carry this field. */
  showSourceDescription?: boolean;
}

export function VendorErrorCodeCatalogSections({
  record,
  classificationMarginBottom = false,
  showSourceDescription = false,
}: VendorErrorCodeCatalogSectionsProps) {
  const { t } = useTranslation('errorManagement');

  return (
    <>
      <ViewDrawerSectionHeader
        tone={SECTION_TONES.identity}
        label={t('viewDrawer.sections.identity')}
      />
      <Descriptions
        size="small"
        column={1}
        bordered
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item label={t('drawer.manufacturer')}>
          {formatDisplayValue(record.manufacturer?.name)}
        </Descriptions.Item>
        <Descriptions.Item label={t('drawer.assetType')}>
          {record.assetType ? (
            <Tag color="cyan" style={{ marginInlineEnd: 0 }}>
              {`${record.assetType.name} (${record.assetType.code})`}
            </Tag>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('drawer.errorCode')}>
          <Typography.Text
            code
            style={{
              color: 'var(--evimo-blue)',
              background:
                'color-mix(in srgb, var(--evimo-blue) 8%, transparent)',
              border: 'none',
            }}
          >
            {record.errorCode}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label={t('drawer.vendorErrorCode')}>
          {formatDisplayValue(record.vendorErrorCode)}
        </Descriptions.Item>
      </Descriptions>

      <ViewDrawerSectionHeader
        tone={SECTION_TONES.diagnostics}
        label={t('viewDrawer.sections.diagnostics')}
      />
      <Descriptions
        size="small"
        column={1}
        bordered
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item label={t('drawer.matchType')}>
          {formatDisplayValue(record.matchType)}
        </Descriptions.Item>
        <Descriptions.Item label={t('drawer.matchKey')}>
          {formatDisplayValue(record.matchKey)}
        </Descriptions.Item>
        <Descriptions.Item label={t('drawer.info')}>
          {formatDisplayValue(record.info)}
        </Descriptions.Item>
        <Descriptions.Item label={t('drawer.firmwareVersion')}>
          {formatDisplayValue(record.firmwareVersion)}
        </Descriptions.Item>
        <Descriptions.Item label={t('drawer.description')}>
          {formatDisplayValue(record.description)}
        </Descriptions.Item>
        {showSourceDescription ? (
          <Descriptions.Item label={t('drawer.sourceDescription')}>
            {formatDisplayValue(record.sourceDescription)}
          </Descriptions.Item>
        ) : null}
      </Descriptions>

      <ViewDrawerSectionHeader
        tone={SECTION_TONES.classification}
        label={t('viewDrawer.sections.classification')}
      />
      <Descriptions
        size="small"
        column={1}
        bordered
        style={classificationMarginBottom ? { marginBottom: 24 } : undefined}
      >
        <Descriptions.Item label={t('drawer.parameter')}>
          {formatDisplayValue(record.parameter)}
        </Descriptions.Item>
        <Descriptions.Item label={t('drawer.state')}>
          {formatDisplayValue(record.state)}
        </Descriptions.Item>
        <Descriptions.Item label={t('drawer.impactedComponent')}>
          {formatDisplayValue(record.impactedComponent)}
        </Descriptions.Item>
        <Descriptions.Item label={t('drawer.category')}>
          {record.category ? (
            <Tag color="purple" style={{ marginInlineEnd: 0 }}>
              {record.category}
            </Tag>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('drawer.severity')}>
          {record.severity ? (
            <Tag
              color={severityTagColor(record.severity)}
              style={{ marginInlineEnd: 0 }}
            >
              {record.severity}
            </Tag>
          ) : (
            '—'
          )}
        </Descriptions.Item>
      </Descriptions>
    </>
  );
}
